import {
  EuiCallOut,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiPanel,
  EuiSpacer,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { useEffect, useRef, useState } from "react";
import {
  AnketaResourcesEnum,
  InviteUser,
  KeycloakCreationRequest,
  KeycloakInvite,
  useAnketaI18n,
  useApi,
  useToast,
  Vpc,
} from "../../../";
import { AnketaResponse } from "../../../api/types";
import { useAnketaAuth } from "../../../auth/context";
import {
  AnketaQuestionnaireBase,
  NetBoxDeviceWithInventory,
} from "shared/index";
import {
  AnketaQuestionnaire,
  OpsanioCatalog,
  OpsanioCreateContactDefaults,
  OpsanioWaves,
} from "../../";
import { useAnketa } from "src/questionnaires/components/context_provider";
import { Catalog } from "anketa-core/types";
import { CatalogObject } from "anketa-core/catalog_object";

type StepStatus =
  | "pending"
  | "running"
  | "success"
  | "partial"
  | "error"
  | "skipped";

interface Step {
  labelKey: string;
  status: StepStatus;
  errorMessage?: string;
}

export const QuestionnaireModal = ({
  catalog,
  userToAssignToQuestionnaire,
  organizationToCreate,
  unassignedNewUsers,
  updateCatalog,
  vpcName,
  contacts,
}: {
  catalog: AnketaQuestionnaireBase;
  userToAssignToQuestionnaire: string[];
  organizationToCreate: Array<{
    organization: KeycloakCreationRequest;
    users: InviteUser[];
  }>;
  unassignedNewUsers: Array<KeycloakInvite>;
  updateCatalog: (c: Catalog) => void;
  vpcName: string;
  contacts: OpsanioCreateContactDefaults;
}): JSX.Element => {
  const { Post, Get, Put } = useApi();
  const { addToast } = useToast();
  const { keycloak, checkPermission, user } = useAnketaAuth();
  const { translate } = useAnketaI18n();
  const ctx = useAnketa();

  const isMounted = useRef(true);

  const [steps, setSteps] = useState<Step[]>([
    { labelKey: "anketa.creation.step.questionnaire", status: "pending" },
    { labelKey: "anketa.creation.step.organizations", status: "pending" },
    { labelKey: "anketa.creation.step.assign", status: "pending" },
    {
      labelKey: "anketa.creation.step.questionnaire.defaults",
      status: "pending",
    },
  ]);

  const [isDone, setIsDone] = useState(false);
  const [hasFatalError, setHasFatalError] = useState(false);

  const getLatestWave = async (): Promise<OpsanioWaves> => {
    try {
      const response = await Get<AnketaResponse<NetBoxDeviceWithInventory[]>>({
        path: `v2/${AnketaResourcesEnum.opsanio}/appliances/wave/next`,
        responseType: "json",
        token: keycloak.token,
      });
      if (response.success && response.data) {
        return (response.data.data as any).leastUsedEnv;
      }
      return "wave3";
    } catch (error) {
      console.log("[NetBox] Error", error);
      return "wave1";
    }
  };
  const OpsanioDefaultValues = async (): Promise<{
    agents: {
      id: string;
      macs: string[];
      redfishMac: string;
      serialNumber: string;
    }[];
    vpc: { vpcs: Vpc[]; tenantIds: number[] };
    vpcName: string;
    nextWave: OpsanioWaves;
    contacts: OpsanioCreateContactDefaults;
  }> => {
    const getDevices = async (): Promise<NetBoxDeviceWithInventory[]> => {
      try {
        const baseParams =
          "getFree=true&includeInventory=false&includeInterfaces=true";

        const path = `v2/netbox/devices?${baseParams}`;
        const response = await Get<AnketaResponse<NetBoxDeviceWithInventory[]>>(
          {
            path,
            responseType: "json",
            token: keycloak.token,
          },
        );
        if (response.success && response.data) {
          return response.data.data;
        }
        console.log("[NetBox] Error", response);
        return [];
      } catch (error) {
        console.log("[NetBox] Error", error);
        return [];
      }
    };

    const getNextFreeVpc = async (): Promise<{
      vpcs: Vpc[];
      tenantIds: number[];
    }> => {
      try {
        const response = await Get<
          AnketaResponse<{ vpcs: Vpc[]; tenantIds: number[] }>
        >({
          path: `v2/gitlab/network/free?top=1`,
          responseType: "json",
          token: keycloak.token,
        });
        if (response.success && response.data) {
          return response.data.data;
        }
        throw new Error(
          response.error?.message || "Failed to retrieve free networks",
        );
      } catch (error) {
        throw error;
      }
    };

    const nextWave = await getLatestWave();
    const [freeDevices, vpc] = await Promise.all([
      getDevices(),
      getNextFreeVpc(),
    ]);

    const agents = freeDevices.map((device) => {
      const macs: string[] = [];
      let redfishMac: string = "";
      const serialNumber: string = device.serial;
      const REDFISH_NAMES = ["ilo", "idrac", "bmc", "mgmt", "redfish", "ipmi"];

      if (device.interfaces) {
        const parentGroups: Record<string, string[]> = {};

        for (const iface of device.interfaces) {
          const name = iface.name?.toLowerCase() ?? "";
          const isRedfishNic =
            iface.mgmt_only &&
            iface.mac_address &&
            iface.mac_address.trim() !== "" &&
            REDFISH_NAMES.some((n) => name.includes(n));

          if (isRedfishNic) {
            if (!redfishMac) {
              redfishMac = iface.mac_address.trim();
            }
          } else if (iface.mac_address && iface.mac_address.trim() !== "") {
            const parent = iface.description || "unknown";
            if (!parentGroups[parent]) {
              parentGroups[parent] = [];
            }
            parentGroups[parent].push(iface.mac_address.trim());
          }
        }

        const groupWithTwo = Object.values(parentGroups).find(
          (macList) => macList.length >= 2,
        );
        if (groupWithTwo) {
          macs.push(...groupWithTwo.slice(0, 2));
        }
      }

      return {
        id: device.id.toString(),
        macs,
        redfishMac,
        serialNumber,
      };
    });
    return {
      agents,
      vpc,
      vpcName,
      nextWave,
      contacts,
    };
  };

  const insertBasicValuesToDb = async (
    catalogId: string,
    questionnaireId: string,
    defaultValues: {
      agents: {
        id: string;
        macs: string[];
        redfishMac: string;
        serialNumber: string;
      }[];
      vpc: {
        vpcs: Vpc[];
        tenantIds: number[];
      };
      vpcName: string;
      nextWave: OpsanioWaves;
      contacts: OpsanioCreateContactDefaults;
    },
  ) => {
    if (!catalogId || !questionnaireId) {
      console.warn(
        "[insertBasicValuesToDb] Missing catalogId or questionnaireId, aborting",
      );
      return;
    }

    const patchRoute = `v2/${AnketaResourcesEnum.questionnaires}/${catalogId}/${questionnaireId}`;

    const fetchResponse = await Get<AnketaQuestionnaire>({
      path: patchRoute,
      responseType: "json",
      token: keycloak.token,
    });

    if (!fetchResponse.success || !fetchResponse.data) {
      const errMsg =
        fetchResponse.error?.message ??
        `Failed to fetch questionnaire (status: ${fetchResponse.status})`;
      console.error("[insertBasicValuesToDb] Fetch failed:", {
        status: fetchResponse.status,
        error: fetchResponse.error,
      });
      throw new Error(errMsg);
    }

    const { data } = fetchResponse;

    let newCatalog: CatalogObject | null = null;

    if (data.questionnaireCatalog === "opsanio") {
      newCatalog = OpsanioCatalog.createInstance(
        data.id,
        data.questionnaireCatalog,
        data.members,
        checkPermission,
        data.content,
        keycloak.token,
        user,
        true,
        defaultValues,
      );
      newCatalog.save();
    }

    if (!newCatalog) {
      console.error("[insertBasicValuesToDb] newCatalog is null, aborting");
      throw new Error("Failed to create catalog instance");
    }

    updateCatalog(newCatalog);
    newCatalog.clone();

    if (data.content === null) {
      const plainObject = JSON.stringify(newCatalog.factsToObject());

      const putResponse = await Put<string>({
        path: patchRoute,
        responseType: "json",
        token: keycloak.token,
        value: { newContent: plainObject },
      });

      if (!putResponse.success) {
        const errMsg =
          putResponse.error?.message ??
          `Failed to write initial facts (status: ${putResponse.status})`;
        console.error("[insertBasicValuesToDb] Put failed:", {
          status: putResponse.status,
          error: putResponse.error,
        });
        throw new Error(errMsg);
      }
    } else {
      ctx.catalog.factsFromObject(data.content);
      newCatalog.factsFromObject(data.content);
    }
  };

  const updateStep = (
    index: number,
    status: StepStatus,
    errorMessage?: string,
  ) => {
    if (!isMounted.current) return;
    setSteps((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], status, errorMessage };
      return next;
    });
  };
  const runWorkflow = async () => {
    updateStep(0, "running");
    setHasFatalError(false);

    try {
      const questionnaireRes = await Post<AnketaResponse<{ id: string }>>({
        path: `v2/${AnketaResourcesEnum.questionnaires}`,
        responseType: "json",
        token: keycloak.token,
        value: catalog,
      });

      if (!questionnaireRes.success || !questionnaireRes.data) {
        const msg =
          questionnaireRes.error?.message || "Questionnaire creation failed";
        updateStep(0, "error", msg);
        setHasFatalError(true);
        return;
      }
      const questionnaireId = questionnaireRes.data.data.id;
      updateStep(0, "success");

      if (organizationToCreate.length === 0) {
        updateStep(1, "skipped");
      } else {
        updateStep(1, "running");
        const orgResults = await Promise.all(
          organizationToCreate.map((org) =>
            Post<AnketaResponse<string>>({
              path: `v2/${AnketaResourcesEnum.organizations}?catalogId=${catalog.id}&questionnaireId=${questionnaireId}`,
              responseType: "json",
              token: keycloak.token,
              value: org,
            }),
          ),
        );

        const failedOrgs = orgResults.filter((r) => !r.success);
        if (failedOrgs.length > 0) {
          console.error(
            "[runWorkflow] organization creation failed:",
            failedOrgs.map((r) => ({ status: r.status, error: r.error })),
          );
          const describeError = (
            r: AnketaResponse<AnketaResponse<string>>,
          ): string => {
            const d = r.error?.details;
            const detailText =
              typeof d === "string"
                ? d
                : Array.isArray(d)
                  ? d
                      .map((issue: any) =>
                        issue?.path && issue?.message
                          ? `${issue.path.join(".")}: ${issue.message}`
                          : JSON.stringify(issue),
                      )
                      .join(", ")
                  : undefined;
            return (
              detailText ?? r.error?.message ?? `status ${r.status ?? "?"}`
            );
          };
          const detail = failedOrgs.map(describeError).join("; ");
          updateStep(
            1,
            "error",
            `One or more organizations failed to create: ${detail}`,
          );
          setHasFatalError(true);
          return;
        }
        updateStep(1, "success");
      }

      updateStep(2, "running");
      const newUsersEmail = unassignedNewUsers.map((u) => u.user.email);
      const allUsers = userToAssignToQuestionnaire.concat(newUsersEmail);

      const assignRes = await Post<AnketaResponse<any>>({
        path: `v2/${AnketaResourcesEnum.questionnaires}/member`,
        responseType: "json",
        token: keycloak.token,
        value: { questionnaireId, usersEmails: allUsers },
      });

      if (!assignRes.success) {
        updateStep(
          2,
          "error",
          assignRes.error?.message || "Member assignment failed",
        );
        setHasFatalError(true);
        return;
      }

      updateStep(2, "success");

      updateStep(3, "running");
      try {
        const defaultValues = await OpsanioDefaultValues();

        await insertBasicValuesToDb(catalog.id, questionnaireId, defaultValues);
        updateStep(3, "success");
      } catch (error: any) {
        updateStep(3, "error", error.message);
      }

      if (isMounted.current) {
        setIsDone(true);
        addToast({
          title: "Workflow Complete",
          color: "success",
          text: "Process finished successfully.",
        });
        setTimeout(() => {
          if (isMounted.current) {
            const baseUrl = window.location.origin;
            window.location.href = `${baseUrl}/questionnaires/${catalog.id}/${questionnaireId}`;
          }
        }, 3000);
      }
    } catch (e: any) {
      console.error("Workflow Exception:", e);
      addToast({ title: "Critical Error", color: "danger", text: e.message });
    }
  };

  const getHealthColor = (status: StepStatus) => {
    switch (status) {
      case "pending":
        return "subdued";
      case "running":
        return "primary";
      case "success":
        return "success";
      case "partial":
        return "warning";
      case "error":
        return "danger";
      case "skipped":
        return "subdued";
    }
  };

  const getStatusIcon = (status: StepStatus) => {
    switch (status) {
      case "pending":
        return "clock";
      case "running":
        return "loading";
      case "success":
        return "checkInCircleFilled";
      case "partial":
        return "alert";
      case "error":
        return "errorFilled";
      case "skipped":
        return "minusInCircle";
    }
  };

  const getStepLabel = (step: Step) => {
    return translate(step.labelKey) || step.labelKey;
  };

  useEffect(() => {
    isMounted.current = true;
    runWorkflow();
    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <EuiPanel color="subdued" paddingSize="l">
      <EuiFlexGroup
        alignItems="center"
        gutterSize="s"
        style={{ marginBottom: 16 }}
      >
        <EuiFlexItem grow={false}>
          <EuiIcon
            type={
              isDone
                ? "checkInCircleFilled"
                : hasFatalError
                  ? "errorFilled"
                  : "gear"
            }
            color={isDone ? "success" : hasFatalError ? "danger" : "primary"}
            size="l"
          />
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiTitle size="xs">
            <h3>
              {isDone
                ? translate("anketa.creation.done") ||
                  "Questionnaire created successfully"
                : hasFatalError
                  ? translate("anketa.creation.failed") || "Creation failed"
                  : translate("anketa.creation.inProgress") ||
                    "Creating questionnaire…"}
            </h3>
          </EuiTitle>
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiFlexGroup direction="column" gutterSize="s">
        {steps.map((step, idx) => (
          <EuiFlexItem key={idx}>
            <EuiFlexGroup
              alignItems="flexStart"
              gutterSize="s"
              responsive={false}
            >
              <EuiFlexItem grow={false} style={{ paddingTop: 2 }}>
                <EuiIcon
                  type={getStatusIcon(step.status)}
                  color={getHealthColor(step.status)}
                  size="m"
                />
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiText
                  size="s"
                  color={
                    step.status === "skipped"
                      ? "subdued"
                      : step.status === "error"
                        ? "danger"
                        : step.status === "partial"
                          ? "warning"
                          : "default"
                  }
                >
                  <span
                    style={{
                      textDecoration:
                        step.status === "skipped" ? "line-through" : "none",
                    }}
                  >
                    {getStepLabel(step)}
                    {step.status === "skipped" && (
                      <span
                        style={{ marginLeft: 6, fontSize: 11, opacity: 0.6 }}
                      >
                        ({translate("anketa.creation.skipped") || "skipped"})
                      </span>
                    )}
                    {step.status === "partial" && (
                      <span
                        style={{ marginLeft: 6, fontSize: 11, opacity: 0.8 }}
                      >
                        ({translate("anketa.creation.partial") || "partial"})
                      </span>
                    )}
                  </span>
                </EuiText>
                {(step.status === "error" || step.status === "partial") &&
                  step.errorMessage && (
                    <EuiText
                      color={step.status === "partial" ? "warning" : "danger"}
                      size="xs"
                    >
                      {step.errorMessage.split("\n").map((line, i) => (
                        <p
                          key={i}
                          style={{ margin: "2px 0 0", fontFamily: "monospace" }}
                        >
                          {line}
                        </p>
                      ))}
                    </EuiText>
                  )}
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlexItem>
        ))}
      </EuiFlexGroup>

      {isDone && (
        <>
          <EuiSpacer size="m" />
          <EuiCallOut
            title={
              translate("anketa.creation.done.hint") ||
              "All steps completed. You may now close this dialog."
            }
            color="success"
            iconType="checkInCircleFilled"
            size="s"
          />
        </>
      )}
    </EuiPanel>
  );
};
